import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Auth } from "@aws-amplify/auth";
import { Amplify } from "@aws-amplify/core";
import { AlertController, Events } from "ionic-angular";
import Company from "models/company-details/company";
import { NavigationService } from "navigation/navigation.service";
import { AppStatus } from "providers/app-status/app-status";
import { ConfigurationService } from "providers/configuration/configuration.service";
import { MobileUtilsProvider } from "providers/mobile-utils/mobile-utils";
import { BehaviorSubject } from "rxjs";
import { take } from "rxjs/operators";
import { StateManagementService } from "../../providers/state-management/state-management.service";
import { UtilityService } from "../../providers/utility-service/utility-service";
import { IOrganization, IUser, IUserCredential } from "./user.model";

@Injectable()
export class AuthService {
  private user: IUser | null;
  private userCredential: IUserCredential | null;
  public authenticationSubject: BehaviorSubject<boolean>;
  private userChanged: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private _loggedInUser = new BehaviorSubject<IUser>(null);
  private orgTimezone: string;
  private isAlertPresented: boolean = false;

  // SUP-20080 (#1 proactive refresh): keep the access token fresh on a timer so
  // requests never go out on a dying/absent token. This eliminates the stale-token
  // race class (status-0 / 401 / "Missing required request parameters: [Authorization]")
  // that surfaced as blank data (NOVA-1933) — the app no longer waits for a request to
  // FAIL before refreshing. It does NOT extend the refresh token's own lifetime; a hard
  // refresh-token expiry is handled by the reactive interceptor path (and, once shipped,
  // the graceful in-place re-auth), never by this silent timer.
  private proactiveRefreshTimer: any = null;
  // Refresh this far before the access token's expiry. Access tokens are 60 min, so a
  // 5-min skew leaves comfortable headroom while keeping the token continuously valid.
  private static readonly PROACTIVE_REFRESH_SKEW_MS = 5 * 60 * 1000;

  constructor(
    public appStatus: AppStatus,
    private configService: ConfigurationService,
    private events: Events,
    private http: HttpClient,
    private mobileUtils: MobileUtilsProvider,
    private navService: NavigationService,
    private alertCtrl: AlertController,
    private utilityService: UtilityService,
    private stateManagementService: StateManagementService
  ) {
    let clientId, redirect, logoutRedirect, authServerUrl, assessmentUrl;
    this.authenticationSubject = new BehaviorSubject<boolean>(false);

    // Configure Amplify and load user on first config emission
    this.configService.configSubject$.pipe(take(1)).subscribe((config: any) => {
      Amplify.configure({
        Auth: this.configService.getConfig().cognito,
      });
      this.loadCurrentUser();
    });

    // Track config values
    this.configService.configSubject$.subscribe((config: any) => {
      redirect = configService.getConfig().redirectUrl;
      logoutRedirect = configService.getConfig().logoutUrl;
      clientId = configService.getConfig().clientId;
      authServerUrl = configService.getConfig().authServerUrl;
      assessmentUrl = configService.getConfig().apiUrl;
    });

    // Update RUM with user details
    this.UserInfoChange.subscribe((user) => {
      if (user && this.configService.rum) {
        this.configService.rum.setUser({
          id: user.userId,
          name: user.name,
          email: user.email,
          tenantId: user.tenantId,
          tenantName: user.tenantName,
        });
      }
    });
  }

  // Method to initiate forgot password process
  public forgotPassword(email: string): Promise<any> {
    return Auth.forgotPassword(email.toLowerCase());
  }

  // Method to confirm forgot password with a code and new password
  public confirmForgotPassword(
    email: string,
    code: string,
    newPassword: string
  ): Promise<any> {
    return Auth.forgotPasswordSubmit(email.toLowerCase(), code, newPassword);
  }

  // Method to set the logged-in user
  setLoggedInUser(value: IUser) {
    this._loggedInUser.next(value);
  }

  // Getter for logged-in user observable
  get loggedInUser() {
    return this._loggedInUser.asObservable();
  }

  getOrgTimezone(): any {
    return this.orgTimezone;
  }

  set UserInfoChange(user) {
    this.userChanged.next(user);
  }
  // Getter for user information change observable
  get UserInfoChange(): BehaviorSubject<any> {
    return this.userChanged;
  }

  // Method to check if a user is logged in
  isLoggedIn(): boolean {
    return this.getUser() != null;
  }

  // Method to load the current user
  private loadCurrentUser(): void {
    Auth.currentAuthenticatedUser()
      .then((cognitoUser: any) => {
        this.mapCognitoUser(cognitoUser.signInUserSession); // Map Cognito user to internal user model
        this.authenticationSubject.next(true);
        localStorage.removeItem(this.mobileUtils.mobileDataDownloadedAtKey);
        this.scheduleProactiveRefresh(); // SUP-20080 (#1): start the keep-alive timer on a restored session
      })
      .catch((error: any) => {
        this.setUser(null);
        this.authenticationSubject.next(false);
        // NOVA-1933: a session that RESOLVED but was empty/partial (mapCognitoUser
        // threw) means the app is running unauthenticated — force a clean re-login
        // instead of silently continuing header-less. A normal "no authenticated
        // user" rejection has no code and keeps the existing behavior (route guards
        // send the user to sign-in; no false "Session Expired" alert).
        if (error && error.code === "EMPTY_COGNITO_SESSION") {
          this.signOut(
            "Your session has expired. Please log in again to continue."
          );
        }
        // NOVA-1470: an unconfigured account (empty roles claim) on a restored
        // session — surface the friendly message instead of running blank.
        if (error && error.code === "ACCOUNT_NOT_CONFIGURED") {
          this.signOut(error.message);
        }
      });
  }

  // Method to get roles by organization ID
  // getRolesByOrgId(orgs: any, orgId: number): string {
  //   for (let org of orgs) {
  //     if (org.OrgId === orgId && org.Roles) {
  //       return JSON.stringify(org.Roles);
  //     }
  //   }
  //   return "";
  // }

  public signUp(user: IUserCredential): Promise<any> {
    return Auth.signUp({
      username: user.email.toLowerCase(),
      password: user.password,
    });
  }

  public confirmSignUp(user: IUserCredential): Promise<any> {
    return Auth.confirmSignUp(user.email.toLowerCase(), user.code);
  }

  // Method to update user information
  private updateUserInfo(accessToken) {
    this.userCredential.accessToken = accessToken;
  }

  // Method to sign in a user
  public signIn(user: IUserCredential): Promise<any> {
    return Auth.signIn(user.email.toLowerCase(), user.password).then(
      (cognitoUser: any) => {
        this.mapCognitoUser(cognitoUser.signInUserSession); // Map Cognito user to internal user model
        this.authenticationSubject.next(true);
        window.localStorage.loggedInUserEmail = this.getUser().email;
        this.scheduleProactiveRefresh(); // SUP-20080 (#1): start the keep-alive timer
        return null;
      }
    );
  }

  // Method to decode roles
  decodeOrganizations(encodedOrgs: string): IOrganization[] {
    let organizations: any = null;
    try {
      const decodedOrgs = atob(encodedOrgs || "");
      organizations = decodedOrgs ? JSON.parse(decodedOrgs) : null;
    } catch (e) {
      organizations = null;
    }
    // NOVA-1470: an empty/unparseable roles claim means the account's org and
    // role setup never reached the token (empty OrgRoles, or a CurrentOrgId
    // with no OrgRoles match upstream). Fail with a tagged, human-readable
    // error the sign-in page can display instead of the raw
    // "Unexpected end of JSON input".
    if (!Array.isArray(organizations) || organizations.length === 0) {
      const notConfigured = new Error(
        "Your account is not fully configured. Please contact your administrator."
      );
      (notConfigured as any).code = "ACCOUNT_NOT_CONFIGURED";
      throw notConfigured;
    }
    return this.convertPropsToLowerCase(organizations);
  }

  public getAuthToken(): Promise<any> {
    return Auth.currentSession();
  }

  // Method to sign out a user
  public signOut(messageError?: string): Promise<any> {
    this.utilityService.decreaseSpinnerCount();
    if (messageError) {
      if (!this.isAlertPresented) {
        this.stateManagementService.resetState();
        this.showSessionExpiredAlert();
      }
      return Promise.resolve(null);
    }
    return Auth.signOut({ global: true })
      .then(() => {
        localStorage.clear();
        sessionStorage.clear();
        this.stateManagementService.resetState();
        this.startAuthentication(messageError);
      })
      .catch((error) => {
        localStorage.clear();
        sessionStorage.clear();
        this.stateManagementService.resetState();
        this.startAuthentication(messageError);
      });
  }

  private showSessionExpiredAlert() {
    // SUP-19982: guard INSIDE the factory. isAlertPresented was only checked by
    // signOut(messageError) at :192, so the two call sites that actually fire on a
    // session failure (refreshAccessToken's inner :317 and outer catch) each created
    // their own Ionic overlay. One failing request per overlay, each with its own
    // click-blocking backdrop, stacked without bound -> the pulsing, locked window.
    if (this.isAlertPresented) {
      return;
    }
    let alert = this.alertCtrl.create({
      // Without this, a backdrop dismiss runs NEITHER handler and isAlertPresented
      // latches true forever, after which signOut(msg) silently returns without
      // signing out or navigating.
      enableBackdropDismiss: false,
      title: "Session Expired",
      message: "Your session is expired, Please login again to continue.",
      buttons: [
        {
          text: "Cancel",
          handler: () => {
            this.isAlertPresented = false;
          },
        },
        {
          text: "OK",
          handler: () => {
            this.signOut();
            this.isAlertPresented = false;
          },
        },
      ],
    });
    this.isAlertPresented = true;
    alert.present();
  }

  // Method to get the authorization header value
  public getAuthorizationHeaderValue(): string | null {
    return this.userCredential ? this.userCredential.accessToken : null;
  }

  // Method to get the current user
  public getUser(): IUser | null {
    return this.user;
  }

  public setUser(user: IUser | null): void {
    this.user = user;
  }

  // it requires when user has access to more than one company
  getCompany(): Company {
    let company = {
      companyId: "",
      name: "",
    };
    let user = this.getUser();
    if (user) {
      let multiCompanies =
        user.organizations.filter((o) => o.orgType === "COMPANY").length > 1;
      if (multiCompanies && user.selectedOrgType === "COMPANY") {
        let currentOrg = user.organizations.find(
          (o) => o.orgId === user.selectedOrg && o.orgType === "COMPANY"
        );
        if (currentOrg) {
          company.companyId = currentOrg.orgId;
          company.name = currentOrg.orgName;
        }
      } else {
        company.companyId = user.orgId;
        company.name = user.orgName;
      }
    }

    return company;
  }

  public getUserCredential(): IUserCredential | null {
    return this.userCredential;
  }

  public setUserCredential(userCredential: IUserCredential | null): void {
    this.userCredential = userCredential;
  }

  // Method to check if a user is authenticated
  public isAuthenticated(): boolean {
    return this.getUser() !== null;
  }

  authenticated(isSignIn?: boolean): boolean {
    if (!this.isAuthenticated() && !isSignIn) {
      return this.startAuthentication();
    }
    return true;
  }

  startAuthentication(errorMessage?: string): boolean {
    this.clearProactiveRefresh(); // SUP-20080 (#1): stop the keep-alive timer when the session ends
    localStorage.removeItem("accessToken");
    this.setUser(null);
    this.setUserCredential(null);
    this.setLoggedInUser(this.getUser());
    this.authenticationSubject.next(false);
    this.navService.goToInternalPage("sign-in-page", { errorMessage });
    return true;
  }

  async refreshAccessToken(
    updateUser?: boolean,
    silent?: boolean
  ): Promise<string> {
    try {
      const cognitoUser = await Auth.currentAuthenticatedUser();
      const currentSession = await Auth.currentSession();
      return new Promise<string>((resolve, reject) => {
        cognitoUser.refreshSession(
          currentSession.getRefreshToken(),
          (err, session) => {
            if (err) {
              // SUP-20080: a proactive (silent) refresh must not pop the Session
              // Expired overlay on a transient failure — the reactive interceptor
              // path still catches the real hard-wall failure on the next request.
              if (!silent) {
                this.showSessionExpiredAlert();
              }
              reject(err);
            } else {
              const newAccessToken = session.getAccessToken().getJwtToken();
              if (newAccessToken && typeof newAccessToken === "string") {
                this.updateUserInfo(newAccessToken);
                // SUP-20080: line up the next proactive refresh against the token
                // we just minted, so the timer chains itself for the life of the
                // session.
                this.scheduleProactiveRefresh();

                // NOVA-2024: the id_token rides in request BODIES (QuickSight
                // embeds) and otherwise only updates at sign-in — without this
                // it ages out in any long-lived app instance while the access
                // token keeps rolling.
                const newIdToken =
                  session.getIdToken && session.getIdToken()
                    ? session.getIdToken().getJwtToken()
                    : null;
                if (newIdToken && this.userCredential) {
                  this.userCredential.id_token = newIdToken;
                }

                if (updateUser) {
                  // updateUser=true, only on org change
                  // Suppress events: the caller (onChange) handles event publishing
                  this.mapCognitoUser(session, true);
                }
                resolve(newAccessToken);
              } else {
                reject(new Error("No valid JWT token found in session."));
              }
            }
          }
        );
      });
    } catch (error) {
      // SUP-20080: same silent guard as the inner refreshSession failure above.
      if (!silent) {
        this.showSessionExpiredAlert();
      }
      const customError = new Error(
        `Token refresh failed: ${error.message || error}`
      );
      (customError as any).code = "REFRESH_TOKEN_FAILED";
      throw customError;
    }
  }

  /**
   * SUP-20080 (#1): schedule a single silent access-token refresh to fire
   * PROACTIVE_REFRESH_SKEW_MS before the current access token expires. On success
   * refreshAccessToken re-arms this timer against the new token, so it chains for
   * the life of the session. Any existing timer is cleared first, so this is safe to
   * call on every sign-in / refresh without stacking timers.
   *
   * A silent failure here is deliberately swallowed: it means the refresh token
   * itself is gone, which the reactive interceptor path (handle401Error) surfaces
   * on the next request. This timer only removes the "token went stale while idle"
   * failure mode; it never signs anyone out on its own.
   */
  private scheduleProactiveRefresh(): void {
    this.clearProactiveRefresh();
    const token = this.getAuthorizationHeaderValue();
    if (!token) {
      return;
    }
    const expiry = this.getTokenExpiryDate(token);
    if (!expiry) {
      return;
    }
    let delay =
      expiry.getTime() - Date.now() - AuthService.PROACTIVE_REFRESH_SKEW_MS;
    if (delay < 0) {
      delay = 0;
    }
    this.proactiveRefreshTimer = setTimeout(() => {
      this.proactiveRefreshTimer = null;
      // silent=true: never pops Session Expired; a hard-wall failure is left for
      // the reactive path. On success refreshAccessToken re-arms the next timer.
      this.refreshAccessToken(false, true).catch(() => {
        /* intentionally silent — see method doc */
      });
    }, delay);
  }

  /** SUP-20080 (#1): cancel any pending proactive refresh (sign-out / re-auth). */
  private clearProactiveRefresh(): void {
    if (this.proactiveRefreshTimer) {
      clearTimeout(this.proactiveRefreshTimer);
      this.proactiveRefreshTimer = null;
    }
  }

  async getValidIdToken(): Promise<string> {
    try {
      // NOVA-2024: the id_token has its own lifetime — gating this on the
      // ACCESS token's expiry let an id_token that expired days earlier pass
      // as fresh (Cognito Identity GetId 400 NotAuthorizedException).
      const credential = this.getUserCredential();
      const idToken = credential && credential.id_token;
      if (!idToken || this.isJwtExpired(idToken)) {
        await this.refreshAccessToken(true);
      }
      return this.getUserCredential().id_token;
    } catch (error) {
      throw new Error(`Token refresh failed: ${error.message || error}`);
    }
  }

  // Method to map Cognito user to internal user model
  private mapCognitoUser(signInUserSession, suppressEvents: boolean = false) {
    const accessToken =
      signInUserSession &&
      signInUserSession.accessToken &&
      signInUserSession.accessToken.jwtToken;
    const idToken =
      signInUserSession &&
      signInUserSession.idToken &&
      signInUserSession.idToken.jwtToken;
    // NOVA-1933: refuse an empty/partial Cognito session instead of persisting an
    // empty access token — an empty token makes AuthorizationHttpInterceptor fire
    // requests with no Authorization header (API Gateway 400 "Missing required
    // request parameters: [Authorization]"), which then fail silently.
    if (!accessToken || !idToken) {
      const emptySessionError = new Error(
        "Cognito session is missing an access or id token."
      );
      (emptySessionError as any).code = "EMPTY_COGNITO_SESSION";
      throw emptySessionError;
    }
    const { name, userId, orgId, currentOrgId, orgName, emailAddress, roles } =
      signInUserSession.accessToken.payload;
    let organizations = this.decodeOrganizations(roles);

    let userCredential: IUserCredential = {
      code: "",
      accessToken: accessToken,
      id_token: idToken,
    };
    this.setUserCredential(userCredential);
    // OPS-2858. isSupportUserAdmin is NOT a token claim - it comes from the
    // POST /user-profile/search response the nav header fetches. This function
    // rebuilds IUser from scratch on every token refresh, and the ORG SWITCHER
    // refreshes with suppressEvents=true, so "user:organizations" is not
    // republished and the nav header never re-fetches. Without carrying the value
    // across the rebuild, switching into a company SILENTLY revoked the Add/Edit
    // buttons on the Support Users tab.
    //
    // Same reasoning as the server-side preserve in
    // user-profile-update/main.go: a value that does not come from the token must
    // not be clobbered by the token-derived rebuild.
    const previousUser = this.getUser();

    let user: IUser = {
      userId: userId,
      qbosUserId: userId,
      name: name,
      email: emailAddress,
      roles: organizations[0]["roles"],
      tenantId: +organizations[0]["orgId"],
      tenantName: organizations[0]["orgName"],
      tenantCode: organizations[0]["abbreviation"],
      orgId: orgId,
      orgName: orgName,
      organizations: [],
      selectedOrgType: organizations[0]["orgType"],
      selectedOrg: currentOrgId,
      isSupportUserAdmin: previousUser
        ? previousUser.isSupportUserAdmin
        : undefined,
    };
    this.setUser(user);
    this.setLoggedInUser(user);
    if (!suppressEvents) {
      this.userChanged.next(user);
      this.events.publish("user:InDashboardPage");
      setTimeout(() => {
        this.events.publish("user:organizations", user);
      }, 0);
    }
  }

  async getJwtToken(): Promise<string | null> {
    try {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    } catch (error) {
      return null;
    }
  }
  public isTokenExpired(): boolean {
    const token = this.getAuthorizationHeaderValue();
    return !token || this.isJwtExpired(token);
  }

  private isJwtExpired(token: string): boolean {
    const expiryDate = this.getTokenExpiryDate(token);
    return expiryDate ? expiryDate < new Date() : true;
  }

  private getTokenExpiryDate(token: string): Date | null {
    const decodedToken = this.decodeToken(token);
    if (!decodedToken || !decodedToken.exp) return null;

    // Convert expiry time from seconds to milliseconds and create a date object
    const date = new Date(0);
    date.setUTCSeconds(decodedToken.exp);
    return date;
  }

  private decodeToken(token: string): any {
    try {
      return JSON.parse(atob(token.split(".")[1]));
    } catch (e) {
      return null;
    }
  }

  convertPropsToLowerCase<T extends object>(array: T[]): T[] {
    return array.map((item) => {
      const result = {} as T;
      for (const key in item) {
        if (Object.prototype.hasOwnProperty.call(item, key)) {
          const newKey = (key.charAt(0).toLowerCase() +
            key.slice(1)) as keyof T;
          const value = item[key];

          if (Array.isArray(value)) {
            // If the value is an array, recursively process if it's an array of objects
            result[newKey] = value.map((element) =>
              typeof element === "object" && element !== null
                ? this.convertPropsToLowerCase([element] as any)[0]
                : element
            ) as any;
          } else if (typeof value === "object" && value !== null) {
            // If the value is an object, recursively process it
            result[newKey] = this.convertPropsToLowerCase([
              value,
            ] as any)[0] as any;
          } else {
            // Otherwise, assign the value as is
            result[newKey] = value;
          }
        }
      }
      return result;
    });
  }

  getChartMedsAuthToken() {
    return this.http.get<string>(
      `${this.configService.getConfig().apiUrl}/curantis-gateway/chartmeds/auth`
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/auth/auth.service.ts